class CelliniNavDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.cnd-overlay');
    this._disableHoverActivation();
    this._updateTop();
    this._bindStickyTracking();
    this._bindTriggers();
    this._bindL1Links();
    this._bindCloseButtons();
    this._bindOutsideClick();
    this._bindHamburger();
    this._bindBackButtons();
    window.addEventListener('resize', () => this._equalizeHeights());
  }

  // Desktop: L1-Liste und L2-Detail auf gleiche Höhe bringen (CS-349).
  // Die beiden Panels sind separat position:fixed -> ohne dies unterschiedlich
  // hoch je nach Produktzahl. Auf die höhere von beiden angleichen (max. Viewport).
  _equalizeHeights() {
    if (this._isMobile()) return;
    const l1 = this.querySelector('.cnd-panel--l1.is-active');
    const l2 = this.querySelector('.cnd-panel--l2.is-active');
    if (!l1 || !l2) return;
    l1.style.height = 'auto'; l2.style.height = 'auto';
    const topVar = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--cnd-top')) || 110;
    const avail = window.innerHeight - topVar - 8;
    let h = Math.max(l1.scrollHeight, l2.scrollHeight) + 1;
    if (h > avail) h = avail;
    l1.style.height = h + 'px'; l2.style.height = h + 'px';
  }

  _isMobile() { return (window.matchMedia && window.matchMedia('(max-width: 989px)').matches) || window.innerWidth < 990; }

  // Kill Horizon's hover/focus dropdown activation entirely. Horizon opens
  // the native dropdown on pointerenter/focus of a menu item; that open/close
  // and its transition was swallowing the trigger click. We block the
  // activating events at WINDOW capture (before Horizon's handlers) — but ONLY
  // the hover/focus event types, never pointerdown/click. Drawer still opens
  // on click, keyboard still works, there is just no hover-open anymore.
  _disableHoverActivation() {
    const inMenu = (e) => {
      const path = e.composedPath ? e.composedPath() : [];
      return path.some(n => n && n.classList && (
        n.classList.contains('menu-list__link') ||
        n.classList.contains('menu-list__list-item')
      ));
    };
    ['pointerenter', 'pointerover', 'mouseenter', 'mouseover', 'focus', 'focusin'].forEach(type => {
      window.addEventListener(type, (e) => {
        if (inMenu(e)) e.stopImmediatePropagation();
      }, true);
    });
  }

  _updateTop() {
    const hc = document.querySelector('header-component');
    if (hc) {
      const bottom = Math.round(hc.getBoundingClientRect().bottom);
      if (bottom > 0) {
        document.documentElement.style.setProperty('--cnd-top', `${bottom}px`);
      }
    }
  }

  // Keep --cnd-top in sync with the header's live bottom edge. The header
  // shrinks on sticky and the announcement bar scrolls away, so a value cached
  // only at open-time leaves a gap between header and the mega-menu panels when
  // the user scrolls with the menu open. rAF-throttled, passive.
  _bindStickyTracking() {
    let ticking = false, settleTimer = null;
    const sync = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => { this._updateTop(); ticking = false; });
      }
      // The header height animates (~0.35s transition). The last scroll frame
      // measures it mid-animation, so re-measure after it settles.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => this._updateTop(), 450);
    };
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    // Catch the exact end of the header's height transition.
    const hc = document.querySelector('header-component');
    if (hc) hc.addEventListener('transitionend', () => this._updateTop());
  }

  // Resolve the L1 panel a header link should open (by URL handle or title slug).
  _panelForLink(el) {
    const href = el.getAttribute('href') || '';
    const urlHandle = href.split('/').pop() || '';
    const textHandle = el.textContent.trim().toLowerCase()
      .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return this.querySelector(`.cnd-panel--l1[data-nav-id="${urlHandle}"]`)
      || this.querySelector(`.cnd-panel--l1[data-nav-id="${textHandle}"]`);
  }

  _bindTriggers() {
    // 1) Suppress Horizon's native dropdown so it never shows behind our drawer.
    this._bindAriaSuppressors();

    // OPEN-ONLY, TIMER-FREE: a header click always opens / switches to that
    // panel and NEVER closes the drawer (closing = overlay, ✕, Escape,
    // outside click). openL1 is idempotent, so even a repeat hit on the
    // active item just re-opens it — clicks never feel "stuck".
    const openFor = (el) => {
      const panel = this._panelForLink(el);
      if (!panel) return false;
      this.openL1(panel.dataset.navId);
      return true;
    };

    // Desktop only: mouse/touch open on POINTERDOWN (delegated on document).
    document.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || this._isMobile()) return;
      const el = e.target.closest && e.target.closest('.menu-list__link');
      if (!el) return;
      if (this._panelForLink(el)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openFor(el);
      }
    }, true);

    // Desktop only: trailing click swallowed; keyboard Enter (detail===0) opens.
    document.addEventListener('click', (e) => {
      if (this._isMobile()) return;
      const el = e.target.closest && e.target.closest('.menu-list__link');
      if (!el || !this._panelForLink(el)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.detail === 0) openFor(el); // keyboard activation
    }, true);

    // Explicit opt-in triggers via [data-cnd-trigger]
    document.querySelectorAll('[data-cnd-trigger]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = el.dataset.cndTrigger;
        const panel = this.querySelector(`.cnd-panel--l1[data-nav-id="${handle}"]`);
        if (panel) this.openL1(panel.dataset.navId);
      });
    });
  }

  _bindAriaSuppressors() {
    document.querySelectorAll('.menu-list__link[aria-haspopup]').forEach(el => {
      let suppressing = false;
      const observer = new MutationObserver(() => {
        if (suppressing) return;
        if (el.getAttribute('aria-expanded') === 'true') {
          suppressing = true;
          el.setAttribute('aria-expanded', 'false');
          suppressing = false;
        }
      });
      observer.observe(el, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
  }

  _bindL1Links() {
    this.querySelectorAll('.cnd-l1-link').forEach(link => {
      link.addEventListener('click', (e) => {
        // Mobile root drill links: open L1 panel
        const mobileNav = link.dataset.mobileNav;
        if (mobileNav && this._isMobile()) {
          e.preventDefault();
          this.openL1Mobile(mobileNav);
          return;
        }
        // L1 → L2: open L2 panel
        const l2Id = link.dataset.l2Id;
        if (l2Id) {
          e.preventDefault();
          this.openL2(l2Id, link);
        }
      });
    });
  }

  _bindCloseButtons() {
    this.overlay?.addEventListener('click', () => this.close());
    this.querySelectorAll('.cnd-close').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
  }

  _bindOutsideClick() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  // Intercept Horizon's hamburger summary on mobile → open CND root panel.
  // e.preventDefault() stops the <details> from toggling open.
  _bindHamburger() {
    var self = this;
    var hdrawer = document.querySelector('header-drawer');
    var details = document.querySelector('header-drawer details.menu-drawer-container') || document.querySelector('header-drawer details');
    var isNativeOpen = function(){
      if (!details) return false;
      return details.hasAttribute('open')
        || details.classList.contains('menu-open')
        || (hdrawer && hdrawer.classList.contains('menu-open'));
    };
    // Fast path: intercept the summary tap before native toggles.
    document.addEventListener('click', function(e){
      if (!self._isMobile()) return;
      var summary = e.target.closest('summary.header__icon--menu');
      if (!summary) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (document.documentElement.classList.contains('cnd-is-open')) self.close(); else self.openRoot();
    }, true);
    // Robust takeover: whenever Horizon's drawer signals "open" (native open
    // attribute OR its menu-open class) on mobile, close it and open CND.
    var takeover = function(){
      if (!self._isMobile()) return;
      if (isNativeOpen()) {
        try { details.open = false; details.removeAttribute('open'); details.classList.remove('menu-open'); if (hdrawer) hdrawer.classList.remove('menu-open'); } catch(e){}
        if (!document.documentElement.classList.contains('cnd-is-open')) self.openRoot();
      }
    };
    if (details) new MutationObserver(takeover).observe(details, { attributes:true, attributeFilter:['open','class'] });
    if (hdrawer) new MutationObserver(takeover).observe(hdrawer, { attributes:true, attributeFilter:['class'] });
  }

  _bindBackButtons() {
    this.querySelectorAll('.cnd-back--to-root').forEach(btn => {
      btn.addEventListener('click', () => this.openRoot());
    });
    this.querySelectorAll('.cnd-back--to-l1').forEach(btn => {
      btn.addEventListener('click', () => {
        const navId = btn.dataset.navId || this._currentL1;
        if (navId) this.openL1Mobile(navId);
      });
    });
  }

  // Mobile entry point: show root panel (all top-level categories).
  openRoot() {
    this._updateTop();
    this.querySelectorAll('.cnd-panel').forEach(p => p.classList.remove('is-active'));
    this.querySelectorAll('.cnd-l1-link').forEach(l => l.classList.remove('is-active'));
    this.overlay?.classList.add('is-active');
    if (!this._isMobile()) document.querySelector('header-component')?.classList.add('cnd-active');
    document.documentElement.classList.add('cnd-is-open', 'cnd-mobile-open');
    const rootPanel = this.querySelector('.cnd-panel--root');
    if (rootPanel) rootPanel.classList.add('is-active');
  }

  openL1(navHandle) {
    this._updateTop();
    this.querySelectorAll('.cnd-panel').forEach(p => p.classList.remove('is-active'));
    this.querySelectorAll('.cnd-l1-link').forEach(l => l.classList.remove('is-active'));
    this.overlay?.classList.add('is-active');
    if (!this._isMobile()) document.querySelector('header-component')?.classList.add('cnd-active');
    document.documentElement.classList.add('cnd-is-open');
    const panel = this.querySelector(`.cnd-panel--l1[data-nav-id="${navHandle}"]`);
    if (!panel) return;
    panel.classList.add('is-active');
    // Auto-open first L2 on desktop only
    if (!this._isMobile()) {
      const first = panel.querySelector('.cnd-l1-link[data-l2-id]');
      if (first) this.openL2(first.dataset.l2Id, first);
      requestAnimationFrame(() => this._equalizeHeights());
    }
  }

  // Mobile: slide to L1 panel (subcategories of a top-level nav item).
  openL1Mobile(navId) {
    this._currentL1 = navId;
    this.querySelectorAll('.cnd-panel').forEach(p => p.classList.remove('is-active'));
    this.querySelectorAll('.cnd-l1-link').forEach(l => l.classList.remove('is-active'));
    this.overlay?.classList.add('is-active');
    // Desktop only: transparent header → bianco when drawer open. Mobile: header stays as-is.
    if (!this._isMobile()) document.querySelector('header-component')?.classList.add('cnd-active');
    document.documentElement.classList.add('cnd-is-open', 'cnd-mobile-open');
    const panel = this.querySelector(`.cnd-panel--l1[data-nav-id="${navId}"]`);
    if (panel) panel.classList.add('is-active');
  }

  openL2(l2Id, triggerLink) {
    this.querySelectorAll('.cnd-panel--l2').forEach(p => p.classList.remove('is-active'));
    this.querySelectorAll('.cnd-l1-link').forEach(l => l.classList.remove('is-active'));
    const panel = this.querySelector(`.cnd-panel--l2[data-l2-id="${l2Id}"]`);
    if (panel) panel.classList.add('is-active');
    if (triggerLink) triggerLink.classList.add('is-active');
    if (!this._isMobile()) requestAnimationFrame(() => this._equalizeHeights());
  }

  close() {
    this.querySelectorAll('.cnd-panel').forEach(p => { p.classList.remove('is-active'); p.style.height = ''; });
    this.querySelectorAll('.cnd-l1-link').forEach(l => l.classList.remove('is-active'));
    this.overlay?.classList.remove('is-active');
    document.querySelector('header-component')?.classList.remove('cnd-active');
    document.documentElement.classList.remove('cnd-is-open', 'cnd-mobile-open');
  }
}
customElements.define('cellini-nav-drawer', CelliniNavDrawer);
